if (!customElements.get('shipping-rate-estimator')) {
  customElements.define(
    'shipping-rate-estimator',
    class ShippingRateEstimator extends HTMLElement {
      connectedCallback() {
        if (this.initialized) return;

        this.form = this.querySelector('[data-shipping-estimator-form]');
        this.country = this.querySelector('[data-shipping-country]');
        this.province = this.querySelector('[data-shipping-province]');
        this.provinceContainer = this.querySelector('[data-shipping-province-container]');
        this.zip = this.querySelector('[data-shipping-zip]');
        this.submitButton = this.querySelector('[data-shipping-submit]');
        this.status = this.querySelector('[data-shipping-status]');
        this.results = this.querySelector('[data-shipping-results]');
        this.disclaimer = this.querySelector('[data-shipping-disclaimer]');

        if (
          !this.form ||
          !this.country ||
          !this.province ||
          !this.provinceContainer ||
          !this.zip ||
          !this.submitButton ||
          !this.status ||
          !this.results
        ) {
          return;
        }

        if (window.Shopify?.CountryProvinceSelector) {
          new window.Shopify.CountryProvinceSelector(this.country.id, this.province.id, {
            hideElement: this.provinceContainer.id,
          });
        }

        this.form.addEventListener('submit', this.onSubmit);
        this.form.addEventListener('input', this.onAddressChange);
        this.form.addEventListener('change', this.onAddressChange);
        this.initialized = true;
      }

      disconnectedCallback() {
        this.cancelEstimate();
        this.form?.removeEventListener('submit', this.onSubmit);
        this.form?.removeEventListener('input', this.onAddressChange);
        this.form?.removeEventListener('change', this.onAddressChange);
        this.initialized = false;
      }

      onAddressChange = () => this.cancelEstimate();

      cancelEstimate() {
        this.requestController?.abort();
        this.requestController = null;
        if (!this.initialized) return;
        this.setLoading(false);
        this.clearResults();
        this.status.textContent = '';
      }

      onSubmit = async (event) => {
        event.preventDefault();
        if (!this.form.reportValidity()) return;

        this.requestController?.abort();
        const controller = new AbortController();
        this.requestController = controller;
        const signal = controller.signal;
        const destination = this.country.value;
        const parameters = this.shippingParameters();

        this.setLoading(true);
        this.clearResults();
        this.dispatchEstimatorEvent('isu:shipping-estimate-requested', { destination_country: destination });

        try {
          const rates = await this.fetchRates(parameters, signal);
          if (signal.aborted || this.requestController !== controller || !this.isConnected) return;

          this.renderRates(rates);
          const presentmentRates = rates.map((rate) => this.priceContext(rate));
          this.dispatchEstimatorEvent('isu:shipping-estimate-succeeded', {
            destination_country: destination,
            rate_count: rates.length,
            lowest_rate: presentmentRates.length
              ? Math.min(...presentmentRates.map((rate) => rate.amount))
              : null,
            currency: presentmentRates[0]?.currency || this.presentationCurrency(),
          });
        } catch (error) {
          if (signal.aborted || this.requestController !== controller || !this.isConnected) return;
          if (error?.name === 'AbortError') return;

          this.status.textContent = error?.code === 'timeout' ? this.dataset.timeout : this.dataset.error;
          this.status.setAttribute('role', 'alert');
          this.dispatchEstimatorEvent('isu:shipping-estimate-failed', {
            destination_country: destination,
            reason: error?.code || 'request_error',
          });
        } finally {
          if (this.requestController === controller) {
            this.setLoading(false);
            this.requestController = null;
          }
        }
      };

      shippingParameters() {
        const parameters = new URLSearchParams();
        parameters.set('shipping_address[country]', this.country.value);
        parameters.set('shipping_address[zip]', this.zip.value.trim());
        if (!this.provinceContainer.hidden && this.province.value) {
          parameters.set('shipping_address[province]', this.province.value);
        }
        return parameters;
      }

      async fetchRates(parameters, signal) {
        const root = String(this.dataset.rootUrl || '/').replace(/\/?$/, '/');
        const requestOptions = {
          headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          signal,
        };
        const prepareResponse = await fetch(`${root}cart/prepare_shipping_rates.json?${parameters}`, {
          ...requestOptions,
          method: 'POST',
        });

        if (!prepareResponse.ok && prepareResponse.status !== 202) throw new Error('prepare_failed');

        for (let attempt = 0; attempt < 8; attempt += 1) {
          await this.wait(350 + attempt * 150, signal);
          const response = await fetch(`${root}cart/async_shipping_rates.json?${parameters}`, requestOptions);
          if (!response.ok) throw new Error('rates_failed');

          const payload = await response.json();
          if (payload !== null) {
            if (!Array.isArray(payload.shipping_rates)) throw new Error('invalid_rates_response');
            return payload.shipping_rates;
          }
        }

        const timeoutError = new Error('rates_timeout');
        timeoutError.code = 'timeout';
        throw timeoutError;
      }

      wait(duration, signal) {
        return new Promise((resolve, reject) => {
          if (signal.aborted) {
            reject(new DOMException('Aborted', 'AbortError'));
            return;
          }
          const onAbort = () => {
            window.clearTimeout(timer);
            reject(new DOMException('Aborted', 'AbortError'));
          };
          const timer = window.setTimeout(() => {
            signal.removeEventListener('abort', onAbort);
            resolve();
          }, duration);
          signal.addEventListener('abort', onAbort, { once: true });
        });
      }

      renderRates(rates) {
        this.status.textContent = '';
        this.status.removeAttribute('role');
        if (!rates.length) {
          this.status.textContent = this.dataset.noRates;
          this.status.setAttribute('role', 'status');
          return;
        }

        const fragment = document.createDocumentFragment();
        rates.forEach((rate) => {
          const item = document.createElement('li');
          item.className = 'isu-shipping-estimator__rate';

          const name = document.createElement('span');
          name.textContent = rate.presentment_name || rate.name || rate.code || '';
          item.append(name);

          const price = document.createElement('strong');
          const priceContext = this.priceContext(rate);
          price.textContent = this.formatPrice(priceContext.amount, priceContext.currency);
          item.append(price);

          if (rate.delivery_date) {
            const delivery = document.createElement('small');
            delivery.textContent = String(this.dataset.deliveryDate || '').replace('{{ date }}', rate.delivery_date);
            item.append(delivery);
          }

          fragment.append(item);
        });

        this.results.replaceChildren(fragment);
        this.results.hidden = false;
        if (this.disclaimer) this.disclaimer.hidden = false;
      }

      formatPrice(value, currency) {
        const amount = Number.parseFloat(value);
        if (!Number.isFinite(amount) || !currency) return String(value || '');

        try {
          return new Intl.NumberFormat(this.dataset.locale || document.documentElement.lang || 'en', {
            style: 'currency',
            currency,
          }).format(amount);
        } catch {
          return `${amount.toLocaleString()} ${currency}`;
        }
      }

      presentationCurrency() {
        return (
          window.Shopify?.currency?.active ||
          this.dataset.presentationCurrency ||
          this.dataset.shopCurrency ||
          null
        );
      }

      priceContext(rate) {
        const rawAmount = rate?.price;
        const amount = typeof rawAmount === 'number' || (typeof rawAmount === 'string' && rawAmount.trim())
          ? Number(rawAmount)
          : NaN;
        if (!Number.isFinite(amount) || amount < 0) throw new Error('invalid_rate_price');
        const shopCurrency = this.dataset.shopCurrency || null;
        const sourceCurrency = rate?.currency || shopCurrency;
        let currency = this.presentationCurrency() || sourceCurrency;
        let presentmentAmount = amount;

        if (sourceCurrency && currency !== sourceCurrency) {
          const conversionRate = Number.parseFloat(window.Shopify?.currency?.rate);
          if (sourceCurrency === shopCurrency && Number.isFinite(conversionRate) && conversionRate > 0) {
            presentmentAmount *= conversionRate;
          } else {
            currency = sourceCurrency;
          }
        }

        return { amount: presentmentAmount, currency };
      }

      clearResults() {
        this.results.replaceChildren();
        this.results.hidden = true;
        if (this.disclaimer) this.disclaimer.hidden = true;
        this.status.removeAttribute('role');
      }

      setLoading(isLoading) {
        this.toggleAttribute('aria-busy', isLoading);
        this.submitButton.disabled = isLoading;
        this.submitButton.querySelector('span').textContent = isLoading
          ? this.dataset.loading
          : this.submitButton.dataset.label;
        if (isLoading) this.status.textContent = this.dataset.loading;
      }

      dispatchEstimatorEvent(name, detail) {
        this.dispatchEvent(new CustomEvent(name, { bubbles: true, detail }));
      }
    }
  );
}
